// Commerce abstraction layer
// TODO: Connect to WooCommerce REST API, Stripe, or other backend.
// Cart state is ephemeral (in-memory); replace with persistent storage as needed.

export interface Product {
  id: string;
  name: string;
  tagline: string;
  description: string;
  type: 'License' | 'Digital' | 'Physical' | 'Service';
  category: string;
  icon: string;
  color: string;
  pricing: PriceModel;
  status: 'Available' | 'Coming Soon' | 'Sold Out';
  featured: boolean;
  features: string[];
  downloadUrl: string;
  demoUrl: string;
}

export interface PriceModel {
  type: 'one-time' | 'subscription' | 'free';
  amount?: number;
  currency?: string;
  period?: string;
  yearlyAmount?: number;
}

export interface CartItem {
  product: Product;
  quantity: number;
}

class CartStore {
  private items: CartItem[] = [];
  private listeners: (() => void)[] = [];

  get(): CartItem[] { return [...this.items]; }

  add(product: Product): void {
    const existing = this.items.find(i => i.product.id === product.id);
    if (existing) {
      existing.quantity++;
    } else {
      this.items.push({ product, quantity: 1 });
    }
    this.notify();
  }

  remove(productId: string): void {
    this.items = this.items.filter(i => i.product.id !== productId);
    this.notify();
  }

  total(): number {
    return this.items.reduce((sum, i) => {
      const price = i.product.pricing.amount ?? 0;
      return sum + price * i.quantity;
    }, 0);
  }

  count(): number {
    return this.items.reduce((sum, i) => sum + i.quantity, 0);
  }

  subscribe(fn: () => void): () => void {
    this.listeners.push(fn);
    return () => { this.listeners = this.listeners.filter(l => l !== fn); };
  }

  private notify(): void {
    this.listeners.forEach(fn => fn());
  }
}

export const cart = new CartStore();

// Format price for display
export function formatPrice(pricing: PriceModel): string {
  if (pricing.type === 'free') return 'Free';
  if (!pricing.amount) return 'Free';
  const symbol = pricing.currency === 'USD' ? '$' : pricing.currency ?? '';
  const amount = `${symbol}${pricing.amount}`;
  if (pricing.type === 'subscription' && pricing.period) {
    return `${amount}/${pricing.period}`;
  }
  return amount;
}

// TODO: initiate checkout — wire up to Stripe Checkout or WooCommerce
export async function initiateCheckout(_items: CartItem[]): Promise<void> {
  console.warn('Commerce backend not yet configured. See src/lib/commerce.ts TODO.');
  alert('Checkout coming soon! Commerce backend not yet connected.');
}
